<template>
  <div
    v-show="isVisible"
    class="c-player-controls"
    :class="{
      'is-active': isActive,
      'c-player-controls--embedded': isEmbedded,
    }"
  >
    <div class="c-player-controls__top">
      <ui-player-age-rating></ui-player-age-rating>
      <ui-player-events-on-timeline v-if="showEvents"></ui-player-events-on-timeline>
    </div>
    <component
      :is="component"
      class="c-player-controls__bar"
      :class="{ 'u-muted': !state.controlsEnabled }"
    ></component>
  </div>
</template>
<script>
  import {
    setPlayerStatus,
    setSeekTo,
    state,
    toggleOptions,
    toggleSeasons,
  } from '@/shared/components/player/store';
  import { OverlayStatus, PlayerStatus, TimeshiftMode } from '@/shared/components/player/enums';
  import UiPlayerFullscreenBtn from '@/shared/components/player/components/controls/components/fullscreen/index.vue';
  import UiPlayerVolume from '@/shared/components/player/components/controls/components/volume/index.vue';
  import UiPlayerProgressBar from '@/shared/components/player/components/controls/components/progress/index.vue';
  import UiPlayerEmbeddedPlayer from '@/shared/components/player/components/controls/layouts/embedded-player.vue';
  import UiPlayerEmbeddedRecommendation from '@/shared/components/player/components/controls/layouts/embedded-recommendation.vue';
  import UiPlayerEpisode from '@/shared/components/player/components/controls/layouts/episode.vue';
  import UiPlayerDefault from '@/shared/components/player/components/controls/layouts/default.vue';
  import UiPlayerNextElement from '@/shared/components/player/components/controls/components/next-element/index.vue';
  import UiPlayerNextEpisode from '@/shared/components/player/components/controls/components/next-episode/index.vue';
  import UiPlayerPreviousElement from '@/shared/components/player/components/controls/components/previous-element/index.vue';
  import UiPlayerTogglePlay from '@/shared/components/player/components/controls/components/toggle-play/index.vue';
  import UiPlayerReplay from '@/shared/components/player/components/controls/components/replay/index.vue';
  import UiPlayerLiveTimeShift from '@/shared/components/player/components/controls/components/live-progress/index.vue';
  import UiPlayerLive from '@/shared/components/player/components/controls/layouts/live.vue';
  import UiPlayerLiveButton from '@/shared/components/player/components/controls/components/live-button/index.vue';
  import UiPlayerAgeRating from '@/shared/components/player/components/age-rating/index.vue';
  import UiPlayerEventsOnTimeline from '@/shared/components/player/components/events-on-timeline/index.vue';
  import { keyHandlerMixin } from '@/shared/components/player/mixins';
  import { ProductType } from '@/shared/enums';
  import { timeFormatExclusive } from '@/index/shared/filters';
  import { numberWithin } from '@/shared/utils';

  export default {
    name: 'UiPlayerControls',
    components: {
      UiPlayerAgeRating,
      UiPlayerEventsOnTimeline,
      UiPlayerLiveButton,
      UiPlayerLive,
      UiPlayerLiveTimeShift,
      UiPlayerTogglePlay,
      UiPlayerReplay,
      UiPlayerNextElement,
      UiPlayerNextEpisode,
      UiPlayerPreviousElement,
      UiPlayerDefault,
      UiPlayerEmbeddedPlayer,
      UiPlayerEmbeddedRecommendation,
      UiPlayerEpisode,
      UiPlayerProgressBar,
      UiPlayerVolume,
      UiPlayerFullscreenBtn,
    },
    mixins: [
      keyHandlerMixin,
    ],
    props: {
      visible: {
        type: Boolean,
        default: false,
      },
      isEmbedded: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        state,
      };
    },
    computed: {
      isLoading() {
        return this.playerStatus === PlayerStatus.LOADING;
      },
      isPaused() {
        return this.playerStatus === PlayerStatus.PAUSE;
      },
      isTimeshiftEnabled() {
        return state.timeshiftMode !== TimeshiftMode.DISABLED;
      },
      isInteraction() {
        return this.overlayStatus === OverlayStatus.INTERACTION;
      },
      isLive() {
        return state.product.type === ProductType.LIVE;
      },
      isVisible() {
        return this.visible && this.playerStatus && !this.isLoading;
      },
      isActive() {
        return this.isInteraction || (this.isEmbedded && this.isPaused);
      },
      overlayStatus() {
        return state.overlayStatus;
      },
      optionsAvailable() {
        const { options } = state;

        return options.audioTracks.length > 1 || options.subtitles.length > 1 || options.profiles.length;
      },
      playerStatus() {
        return state.playerStatus;
      },
      timeElapsed() {
        return timeFormatExclusive(state.progress);
      },
      timeLeft() {
        return timeFormatExclusive(Math.max(0, state.duration - state.progress));
      },
      duration() {
        return timeFormatExclusive(state.duration);
      },
      component() {
        if (this.$route.params.slug === 'recommendations') {
          return UiPlayerEmbeddedRecommendation;
        }

        if (this.isEmbedded) {
          return UiPlayerEmbeddedPlayer;
        }

        if (this.isLive) {
          return UiPlayerLive;
        }

        if (ProductType.EPISODE === state.product.type) {
          return UiPlayerEpisode;
        }

        return UiPlayerDefault;
      },
      isMobileView() {
        return this.$window.isMobileView;
      },
      showEvents() {
        return state.eventsToShow.length;
      },
    },
    methods: {
      togglePlay() {
        const status = this.playerStatus;

        if (status === PlayerStatus.ADVERTS_PAUSE) {
          setPlayerStatus(PlayerStatus.ADVERTS_PLAYING);
          return;
        }

        if (status === PlayerStatus.ADVERTS_PLAYING) {
          setPlayerStatus(PlayerStatus.ADVERTS_PAUSE);
          return;
        }

        if (!state.controlsEnabled) {
          return;
        }

        if (status === PlayerStatus.PAUSE) {
          setPlayerStatus(PlayerStatus.PLAYING);
        } else {
          setPlayerStatus(PlayerStatus.PAUSE);
        }
      },
      seekForward() {
        this.seekBySeconds(10);
      },
      seekBackward() {
        this.seekBySeconds(-10);
      },
      seekBySeconds(amount) {
        if (!state.controlsEnabled) {
          return;
        }

        if (state.product.type === ProductType.LIVE) {
          setSeekTo(-amount);
          return;
        }

        setSeekTo(numberWithin(0, state.duration, state.progress + amount));
      },
      toggleSeasons,
      toggleOptions,
    },
  };
</script>
